import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Building2, CalendarDays, CheckCircle2, MapPin, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PublicAdPlaceholder from "@/components/public/PublicAdPlaceholder";
import PublicBreadcrumbs from "@/components/public/PublicBreadcrumbs";

export default function SeoDetailLayout({ title, description, explanation = [], highlights = [], faqs = [], backTo, backLabel, schemaType = "Article", meta = [], longformContent = null, freshness = null, labels = {} }) {
  const copy = {
    home: "Home",
    publicLabel: "Public Maritime Intelligence",
    explanationHeading: (value) => `What ${value} Means in Practical Sailing`,
    highlightsTitle: "Planning Highlights",
    premiumTitle: "Premium Layer",
    premiumText: "Use this public knowledge as a base, then upgrade to AI-assisted voyage planning, weather routing, risk scoring, and fleet tools.",
    premiumCta: "View Premium Tools",
    faqTitle: "Frequently Asked Questions",
    ...labels,
  };
  useEffect(() => {
    const url = `https://marstick.com${window.location.pathname}`;
    document.title = `${title} | Marstick`;

    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content = description;

    let schema = document.getElementById("schema-programmatic-detail");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "schema-programmatic-detail";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": schemaType,
      name: title,
      headline: title,
      description,
      url,
      mainEntity: faqs.map(faq => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    });

    return () => schema.remove();
  }, [description, faqs, schemaType, title]);

  return (
    <main className="maritime-page-shell">
      <PublicBreadcrumbs
        items={[
          { label: copy.home, href: "/" },
          { label: backLabel?.replace(/^Back to\s+/i, "") || "Overview", href: backTo },
          { label: title },
        ]}
      />

      <section className="rounded-3xl border border-border bg-card p-6 shadow-xl md:p-10">
        <div className="max-w-3xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">
            <MapPin className="h-4 w-4" /> {copy.publicLabel}
          </div>
          <h1 className="text-balance font-heading text-4xl font-bold md:text-6xl">{title}</h1>
          <p className="mt-5 text-pretty text-base leading-7 text-muted-foreground md:text-lg">{description}</p>
          {meta.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {meta.map(item => <span key={item} className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">{item}</span>)}
            </div>
          )}
        </div>
      </section>

      {freshness && (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-3xl border border-border bg-card p-5 shadow-lg"><CalendarDays className="mb-3 h-5 w-5 text-primary" /><p className="text-xs font-black uppercase tracking-wider text-muted-foreground">{freshness.labels.reviewed}</p><p className="mt-2 text-sm font-bold text-foreground">{freshness.reviewed}</p></div>
          <div className="rounded-3xl border border-border bg-card p-5 shadow-lg"><CalendarDays className="mb-3 h-5 w-5 text-primary" /><p className="text-xs font-black uppercase tracking-wider text-muted-foreground">{freshness.labels.effective}</p><p className="mt-2 text-sm font-bold text-foreground">{freshness.effective}</p></div>
          <div className="rounded-3xl border border-border bg-card p-5 shadow-lg"><Building2 className="mb-3 h-5 w-5 text-primary" /><p className="text-xs font-black uppercase tracking-wider text-muted-foreground">{freshness.labels.responsible}</p><p className="mt-2 text-sm font-bold text-foreground">{freshness.responsible}</p></div>
          <div className="rounded-3xl border border-border bg-card p-5 shadow-lg"><RefreshCw className="mb-3 h-5 w-5 text-primary" /><p className="text-xs font-black uppercase tracking-wider text-muted-foreground">{freshness.labels.changed}</p><p className="mt-2 text-sm font-bold leading-6 text-foreground">{freshness.changed}</p></div>
        </section>
      )}

      <PublicAdPlaceholder variant="compact" />

      {longformContent}

      {explanation.length > 0 && (
        <section className="rounded-3xl border border-border bg-card p-6 shadow-xl md:p-8">
          <h2 className="font-heading text-3xl font-bold">{copy.explanationHeading(title)}</h2>
          <div className="mt-5 space-y-5 text-base leading-8 text-muted-foreground">
            {explanation.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>
        </section>
      )}

      <section className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <h2 className="font-heading text-2xl font-bold">{copy.highlightsTitle}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {highlights.map(item => (
                <div key={item} className="flex gap-3 rounded-2xl border border-border bg-background p-4 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <h2 className="font-heading text-2xl font-bold">{copy.premiumTitle}</h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{copy.premiumText}</p>
            <Button asChild className="mt-5 w-full">
              <Link to="/premium">{copy.premiumCta}</Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-3xl font-bold">{copy.faqTitle}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map(faq => (
            <Card key={faq.question}>
              <CardContent className="p-6">
                <h3 className="font-semibold">{faq.question}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </main>
  );
}